import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import { UserModeProvider } from "@/hooks/useUserMode";
import { GlobalStateProvider } from "@/contexts/GlobalStateContext";
import { AccessibilityPanel } from "@/components/accessibility/AccessibilityPanel";
import { Toaster } from "@/components/ui/toaster";
import CookieConsent from "@/components/CookieConsent";
import Index from "./pages/Index";

// Lazy load all pages except Index for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Search = lazy(() => import("./pages/Search"));
const Listings = lazy(() => import("./pages/Listings"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Messages = lazy(() => import("./pages/Messages"));
const Help = lazy(() => import("./pages/Help"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Impressum = lazy(() => import("./pages/Impressum"));
const MyListings = lazy(() => import("./pages/MyListings"));
const CreateListing = lazy(() => import("./pages/CreateListing"));
const EditListing = lazy(() => import("./pages/EditListing"));
const PasswordReset = lazy(() => import("./pages/PasswordReset"));
const CooperativeInfo = lazy(() => import("./pages/CooperativeInfo"));
const CooperativeApplication = lazy(() => import("./pages/CooperativeApplication"));
const EmailConfirmed = lazy(() => import("./pages/EmailConfirmed"));
const EmailVerificationPending = lazy(() => import("./pages/EmailVerificationPending"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const SecurityOverview = lazy(() => import("./pages/SecurityOverview"));
const AITravel = lazy(() => import("./pages/AITravel"));
const AIPricing = lazy(() => import("./pages/AIPricing"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const AdminCooperative = lazy(() => import("./pages/AdminCooperative"));
const AdminSightseeing = lazy(() => import("./pages/AdminSightseeing"));
const HostDashboard = lazy(() => import("./pages/HostDashboard"));
const ListingDashboard = lazy(() => import("./pages/ListingDashboard"));
const BusinessCenter = lazy(() => import("./pages/BusinessCenter"));
const HostServices = lazy(() => import("./pages/HostServices"));
const PaymentTesting = lazy(() => import("./pages/PaymentTesting"));
const ConfirmDeletion = lazy(() => import("./pages/ConfirmDeletion"));
const ConfirmationError = lazy(() => import("./pages/ConfirmationError"));
const FoundingHost = lazy(() => import("./pages/FoundingHost"));
const VerifyBookingEmail = lazy(() => import("./pages/VerifyBookingEmail"));
const CreateTestListings = lazy(() => import("./pages/CreateTestListings"));
const TaxProfile = lazy(() => import("./pages/TaxProfile"));
const ListingReviews = lazy(() => import("./pages/ListingReviews"));
const DraftRedirect = lazy(() => import("./pages/DraftRedirect"));
const GuestBookingPage = lazy(() => import("./pages/GuestBookingPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <GlobalStateProvider>
          <UserModeProvider>
            <BrowserRouter>
              <div className="min-h-screen bg-background">
                <AccessibilityPanel />
                <Toaster />
                <CookieConsent />
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/listings" element={<Listings />} />
                    <Route path="/listing/:id" element={<ListingDetail />} />
                    <Route path="/unterkunft/:slug" element={<ListingDetail />} />
                    <Route path="/listing/:id/reviews" element={<ListingReviews />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/bookings" element={<Bookings />} />
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/my-listings" element={<MyListings />} />
                    <Route path="/create-listing" element={<CreateListing />} />
                    <Route path="/edit-listing/:id" element={<EditListing />} />
                    <Route path="/password-reset" element={<PasswordReset />} />
                    <Route path="/cooperative" element={<CooperativeInfo />} />
                    <Route path="/cooperative/apply" element={<CooperativeApplication />} />
                    <Route path="/email-confirmed" element={<EmailConfirmed />} />
                    <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
                    <Route path="/booking-success" element={<BookingSuccess />} />
                    <Route path="/ai-travel" element={<AITravel />} />
                    <Route path="/ai-pricing" element={<AIPricing />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/impressum" element={<Impressum />} />
                    <Route path="/security" element={<SecurityOverview />} />
                    <Route path="/gruenderhost" element={<FoundingHost />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/users" element={<AdminUsers />} />
                    <Route path="/admin/content" element={<AdminContent />} />
                    <Route path="/admin/cooperative" element={<AdminCooperative />} />
                    <Route path="/admin/sightseeing" element={<AdminSightseeing />} />
                    <Route path="/host-dashboard" element={<HostDashboard />} />
                    <Route path="/listing-dashboard/:id" element={<ListingDashboard />} />
                    <Route path="/business-center" element={<BusinessCenter />} />
                    <Route path="/host-services" element={<HostServices />} />
                    <Route path="/payment-testing" element={<PaymentTesting />} />
                    <Route path="/confirm-deletion" element={<ConfirmDeletion />} />
                    <Route path="/confirmation-error" element={<ConfirmationError />} />
                    <Route path="/verify-booking-email" element={<VerifyBookingEmail />} />
                    <Route path="/create-test-listings" element={<CreateTestListings />} />
                    <Route path="/tax-profile" element={<TaxProfile />} />
                    <Route path="/draft/:draftId" element={<DraftRedirect />} />
                    <Route path="/book/:invitationId" element={<GuestBookingPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </div>
            </BrowserRouter>
          </UserModeProvider>
        </GlobalStateProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
